import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    packages: Array,
    savedPackage: String,
    savedPrice: Number
  }

  static targets = ["package", "price", "discount", "discountedPrice", "includedItems", "includedItemsLead", "pricing"]

  static outlets = ["admin--quotes--marketing-service"]

  #year = ""

  #country = ""

  // A saved quote keeps the price it was quoted at, so only choosing another package takes that package's price.
  connect() {
    this.#updateDiscount()
  }

  // outlet API: called by admin--quotes--option via the marketing package outlet
  // Offers the packages of the auction's year and the agency's country that suit the offer type, plus the package a
  // saved quote already has, and keeps the selection where it is still offered.
  updatePackages(year, country, offerType) {
    this.#year = year
    this.#country = country
    const select = this.packageTarget
    const selected = select.value

    const options = this.packagesValue
      .filter(
        ({ id, year: packageYear, country: packageCountry, offerTypes }) =>
          id === this.savedPackageValue ||
          (packageYear === year && packageCountry === country && (offerType === "" || offerTypes.includes(offerType)))
      )
      .map(({ id, label }) => new Option(label, id))

    select.replaceChildren(...options)
    select.value = selected
    if (select.selectedIndex === -1) select.selectedIndex = 0

    if (select.value !== selected) this.updatePrice()

    this.#updateForPackage()
  }

  updatePrice() {
    this.priceTarget.value = this.#packagePrice
    this.#updateDiscount()
    this.#updateForPackage()
  }

  updateDiscountedPrice() {
    this.discountedPriceTarget.value = this.#price - this.#discount
  }

  get #price() {
    return parseFloat(this.priceTarget.value) || 0
  }

  // A saved quote's own package keeps the price the quote was saved with; any other package costs its current price.
  get #packagePrice() {
    const id = this.packageTarget.value
    if (id === this.savedPackageValue) return this.savedPriceValue

    return this.packagesValue.find(({ id: packageId }) => packageId === id)?.price ?? 0
  }

  get #discount() {
    return parseFloat(this.discountTarget.value) || 0
  }

  // Full marketing is charged as a commission on the total turnover, without a package price or a discount. Every
  // package offers the services of the auction's year and the agency's country. The option hears of the change, as full
  // marketing also waives the online turnover commission.
  #updateForPackage() {
    const fullPackage = this.#selectedPackage?.full ?? false
    this.pricingTarget.hidden = fullPackage
    this.pricingTarget.disabled = fullPackage
    this.#listIncludedItems()
    this.dispatch("change", { detail: { fullPackage } })

    this.adminQuotesMarketingServiceOutlets.forEach(service => {
      service.showFor(this.#year, this.#country)
    })
  }

  // The selected package's items are listed below it. An upgrade package comes on top of the standard marketing, as
  // the quote words it.
  #listIncludedItems() {
    const selectedPackage = this.#selectedPackage
    const upgrade = selectedPackage && !selectedPackage.standard && !selectedPackage.full
    this.includedItemsLeadTarget.textContent = upgrade ? "Standard marketing, plus:" : "Includes:"

    const items = selectedPackage?.includedItems ?? []
    this.includedItemsTarget.replaceChildren(
      ...items.map(text => {
        const item = document.createElement("li")
        item.textContent = text
        return item
      })
    )
  }

  get #selectedPackage() {
    return this.packagesValue.find(({ id }) => id === this.packageTarget.value)
  }

  #updateDiscount() {
    this.discountTarget.setAttribute("max", this.#price)
    this.updateDiscountedPrice()
  }
}
